/**
 * Authentication Route Guard
 * Protects routes that require user authentication
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';

interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children, fallback }) => {
  const { isAuthenticated, sessionBootstrapped } = useAuthStore();
  const location = useLocation();

  // Block the shell until refresh-from-storage resolves so API calls don't race without a Bearer token.
  if (!sessionBootstrapped) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#e3f2fd] to-[#f1f8e9]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1565c0] mx-auto mb-4"></div>
          <p className="text-[#666] dark:text-gray-400 text-lg">Authenticating...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show fallback or children if authenticated
  return <>{fallback || children}</>;
};

export default AuthGuard;